/**
 * @fileoverview One job's card wired to Today's marks (B152): JobCard with its pending tick, busy state, failure words,
 * disclosure and mark handler taken from useTodoMarks. TodoGroups (the to-do groups) and WaterSection (the "Water today?"
 * section) both draw their cards through it, so the wiring is written once.
 *
 * Where: TodoGroups, WaterSection.
 * API: none of its own (the marks are useTodoMarks').
 * Words: none of its own.
 *
 * @module MarkedJobCard
 */

import React from 'react';
import type { BriefTodo, TodoJob } from '@/shared/api/todayBrief';
import { JobCard } from './JobCard';
import type { TodoMarks } from '../lib/useTodoMarks';

/** What the card needs. */
export interface MarkedJobCardProps {
  /** The job, as the server says it. */
  job: TodoJob;
  /** The list's own words. */
  words: BriefTodo['words'];
  /** Marking, from useTodoMarks. */
  marks: TodoMarks;
  /** Its disclosure (why lines and plants) is open. */
  open: boolean;
  /** Opens or closes its disclosure. */
  onOpenChange: (open: boolean) => void;
  /** A job left from yesterday was tapped: offer today, tomorrow or skip; none for any other. */
  onLater?: () => void;
  /** A gardener's own to-do: opens the own to-do sheet from Edit; none for any other. */
  onEdit?: () => void;
  /** Its element, to scroll to it (an email's link or a to-do just added). */
  cardRef?: React.Ref<HTMLDivElement>;
}

/**
 * The set of open jobs after one is opened or closed.
 *
 * @param current - The keys open now.
 * @param key - The job's key.
 * @param open - Whether it is now open.
 * @returns A new set.
 */
export const withOpen = (current: ReadonlySet<string>, key: string, open: boolean): ReadonlySet<string> => {
  const next = new Set(current);
  if (open) next.add(key); else next.delete(key);
  return next;
};

/**
 * One job's card, marks wired.
 *
 * @param props - MarkedJobCardProps.
 */
export const MarkedJobCard: React.FC<MarkedJobCardProps> = ({ job, words, marks, open, onOpenChange, onLater, onEdit, cardRef }) => (
  <JobCard
    job={job}
    words={words}
    pending={marks.pending[job.key]}
    busy={marks.busyKey === job.key}
    failedWords={marks.failed?.key === job.key ? marks.failed.words : null}
    open={open}
    onOpenChange={onOpenChange}
    onMark={(mark) => { void marks.mark(job, mark); }}
    onLater={onLater}
    onEdit={onEdit}
    cardRef={cardRef}
  />
);
